import type { GroupParticipantsApiResponse } from './broadcastContactUtils';

const DEFAULT_TTL_MS = 2 * 60 * 1000;
const EMPTY_TTL_MS = 30 * 1000;

type CacheEntry = {
  response: GroupParticipantsApiResponse;
  expiresAt: number;
};

const store = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<GroupParticipantsApiResponse>>();

function cacheKey(sectorId: number, groupId: number): string {
  return `${sectorId}:${groupId}`;
}

function ttlFor(response: GroupParticipantsApiResponse): number {
  const count = response?.data?.length ?? response?.count ?? 0;
  return count > 0 ? DEFAULT_TTL_MS : EMPTY_TTL_MS;
}

export function getCachedGroupParticipants(
  sectorId: number,
  groupId: number
): GroupParticipantsApiResponse | null {
  const key = cacheKey(sectorId, groupId);
  const hit = store.get(key);
  if (!hit) return null;
  if (Date.now() > hit.expiresAt) {
    store.delete(key);
    return null;
  }
  return hit.response;
}

export function setCachedGroupParticipants(
  sectorId: number,
  groupId: number,
  response: GroupParticipantsApiResponse
): void {
  store.set(cacheKey(sectorId, groupId), {
    response,
    expiresAt: Date.now() + ttlFor(response),
  });
}

export function clearGroupParticipantsCache(sectorId?: number, groupId?: number): void {
  if (sectorId == null) {
    store.clear();
    inflight.clear();
    return;
  }
  if (groupId == null) {
    const prefix = `${sectorId}:`;
    Array.from(store.keys()).forEach((key) => {
      if (key.startsWith(prefix)) store.delete(key);
    });
    Array.from(inflight.keys()).forEach((key) => {
      if (key.startsWith(prefix)) inflight.delete(key);
    });
    return;
  }
  const key = cacheKey(sectorId, groupId);
  store.delete(key);
  inflight.delete(key);
}

/** Dedupes concurrent fetches for the same sector+group. */
export async function fetchGroupParticipantsDeduped(
  sectorId: number,
  groupId: number,
  fetcher: (refresh: boolean, signal?: AbortSignal) => Promise<GroupParticipantsApiResponse>,
  options?: { refresh?: boolean; signal?: AbortSignal; useCache?: boolean }
): Promise<GroupParticipantsApiResponse> {
  const refresh = options?.refresh === true;
  const useCache = options?.useCache !== false;

  if (!refresh && useCache) {
    const cached = getCachedGroupParticipants(sectorId, groupId);
    if (cached) return cached;
  }

  const key = cacheKey(sectorId, groupId);
  if (!refresh && inflight.has(key)) {
    return inflight.get(key)!;
  }

  const task = fetcher(refresh, options?.signal)
    .then((response) => {
      if (useCache) setCachedGroupParticipants(sectorId, groupId, response);
      return response;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, task);
  return task;
}

/** Test helper */
export function __groupParticipantsCacheSize(): number {
  return store.size;
}
